/**
 * RestoreAccessModal — passwordless cross-device entitlement transfer.
 *
 * Step 1: email entry → POST /api/access/send-code (Resend or dev fallback)
 * Step 2: 6-digit code entry → POST /api/access/confirm-code
 * Step 3: success → refresh access status in the parent
 */
import { useEffect, useState } from "react";
import { X, Mail, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { authedAxios } from "../lib/useAccess";

export const RestoreAccessModal = ({ open, onClose, onRestored }) => {
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep("email");
      setEmail("");
      setCode("");
      setDevCode("");
      setError("");
      setBusy(false);
      setRestored(null);
    }
  }, [open]);

  if (!open) return null;

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email.trim()) {
      setError("Enter the email you used at checkout.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await authedAxios().post("/access/send-code", { email });
      setDevCode(data?.dev_code || "");
      setStep("code");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Couldn’t send the code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!code.trim()) {
      setError("Enter the 6-digit code we sent.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await authedAxios().post("/access/confirm-code", {
        email,
        code: code.trim(),
      });
      if (data?.linked) {
        setRestored(data.status);
        setStep("done");
        onRestored?.();
      } else {
        setError("No paid access was found for that email yet.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "That code didn’t match. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="restore-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(42, 36, 32, 0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ss-panel relative w-full max-w-md"
        style={{ borderColor: "var(--ss-rule-line)" }}
      >
        <button
          onClick={onClose}
          data-testid="restore-close-btn"
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-black/5"
          style={{ color: "var(--ss-muted)" }}
        >
          <X className="w-5 h-5" strokeWidth={2.25} />
        </button>

        <div className="p-6 sm:p-7">
          <p className="ss-eyebrow flex items-center gap-2 mb-2" style={{ color: "var(--ss-gold)" }}>
            <KeyRound className="w-3.5 h-3.5" strokeWidth={2.25} />
            Restore access
          </p>
          <h3
            id="restore-title"
            className="ss-display-tight text-2xl sm:text-3xl mb-4 leading-tight"
            style={{ color: "var(--ss-ink)" }}
          >
            {step === "done" ? "You’re back in." : "New phone? New browser?"}
          </h3>

          {step === "email" && (
            <form onSubmit={sendCode}>
              <p className="text-base mb-4" style={{ color: "var(--ss-ink-2)" }}>
                Enter the email you used at checkout. We’ll send a one-time
                code so you can use your remaining reads on this device.
              </p>
              <label className="block mb-3">
                <span className="ss-eyebrow block mb-1.5" style={{ color: "var(--ss-muted)" }}>
                  Email
                </span>
                <div className="flex items-center gap-2 rounded-lg px-3 py-2 border" style={{ background: "var(--ss-card-inset)", borderColor: "var(--ss-rule-line)" }}>
                  <Mail className="w-4 h-4" strokeWidth={2.25} style={{ color: "var(--ss-muted)" }} />
                  <input
                    data-testid="restore-email-input"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 bg-transparent outline-none text-base"
                    style={{ color: "var(--ss-ink)" }}
                  />
                </div>
              </label>
              {error && (
                <div data-testid="restore-error" className="mb-3 text-sm" style={{ color: "var(--ss-red)" }}>
                  {error}
                </div>
              )}
              <button
                data-testid="restore-send-btn"
                type="submit"
                disabled={busy}
                className="ss-cta w-full justify-center"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} /> : null}
                {busy ? "Sending…" : "Send the code"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={confirmCode}>
              <p className="text-base mb-4" style={{ color: "var(--ss-ink-2)" }}>
                We sent a 6-digit code to{" "}
                <strong style={{ color: "var(--ss-ink)" }}>{email}</strong>.
                It expires in 10 minutes.
              </p>
              {devCode && (
                <div
                  data-testid="restore-dev-code"
                  className="mb-4 p-3 rounded-lg text-sm"
                  style={{
                    background: "var(--ss-amber-bg)",
                    borderLeft: "3px solid var(--ss-amber)",
                    color: "var(--ss-ink)",
                  }}
                >
                  <strong>Preview mode:</strong> live email isn’t configured
                  yet. Your code is <span className="ss-mono" style={{ color: "var(--ss-gold-deep)" }}>{devCode}</span>.
                </div>
              )}
              <label className="block mb-3">
                <span className="ss-eyebrow block mb-1.5" style={{ color: "var(--ss-muted)" }}>
                  6-digit code
                </span>
                <input
                  data-testid="restore-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-full text-center ss-mono text-2xl tracking-widest rounded-lg px-3 py-3 border outline-none"
                  style={{ background: "var(--ss-card-inset)", borderColor: "var(--ss-rule-line)", color: "var(--ss-ink)" }}
                />
              </label>
              {error && (
                <div data-testid="restore-error" className="mb-3 text-sm" style={{ color: "var(--ss-red)" }}>
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep("email")}
                  className="ss-ghost-sm flex-1 justify-center"
                  data-testid="restore-back-btn"
                >
                  Change email
                </button>
                <button
                  data-testid="restore-confirm-btn"
                  type="submit"
                  disabled={busy}
                  className="ss-cta flex-1 justify-center"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} /> : null}
                  {busy ? "Checking…" : "Confirm"}
                </button>
              </div>
            </form>
          )}

          {step === "done" && (
            <div data-testid="restore-success">
              <div
                className="mb-4 p-4 rounded-lg flex items-start gap-3"
                style={{
                  background: "var(--ss-green-bg)",
                  borderLeft: "3px solid var(--ss-green-rail)",
                }}
              >
                <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2.25} style={{ color: "var(--ss-green)" }} />
                <div>
                  <p className="ss-eyebrow mb-1" style={{ color: "var(--ss-green)" }}>
                    Access restored
                  </p>
                  <p className="text-base" style={{ color: "var(--ss-ink)" }}>
                    {restored?.tier === "monthly_membership"
                      ? `Monthly Membership · ${restored.reads_remaining} reads this cycle.`
                      : restored?.tier === "fourteen_day_pass"
                      ? `14-Day Pass · ${restored.reads_remaining} reads remaining.`
                      : `Full Read · ${restored?.reads_remaining ?? 0} read${(restored?.reads_remaining ?? 0) === 1 ? "" : "s"} ready.`}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="ss-cta w-full justify-center" data-testid="restore-done-btn">
                Get back to it
              </button>
            </div>
          )}

          <p className="mt-5 text-xs leading-relaxed" style={{ color: "var(--ss-muted)" }}>
            We never link your reads to your email. The code only matches your
            purchase to this device.
          </p>
        </div>
      </div>
    </div>
  );
};
